// src/pages/AssessmentDetails.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { assessmentsApi } from '../services/api';
import PdfOptionsDialog from '../components/PdfOptionsDialog';
import DuplicateAssessmentDialog from '../components/DuplicateAssessmentDialog';
import EditAssessmentMetadataDialog from '../components/EditAssessmentMetadataDialog';

const AssessmentDetails = () => {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [editMetadataDialogOpen, setEditMetadataDialogOpen] = useState(false);

  useEffect(() => {
    loadAssessment();
  }, [assessmentId]);

  const loadAssessment = async () => {
    try {
      const response = await assessmentsApi.getById(assessmentId);
      setAssessment(response.data);
    } catch (error) {
      setError('Failed to load assessment');
      console.error('Error loading assessment:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: { bg: '#e9ecef', color: '#495057' },
      in_progress: { bg: '#fff3cd', color: '#856404' },
      completed: { bg: '#d4edda', color: '#155724' },
      reopened: { bg: '#d1ecf1', color: '#0c5460' }
    };
    const style = styles[status] || styles.draft;
    return (
      <span style={{
        padding: '6px 12px',
        borderRadius: '4px',
        fontSize: '14px',
        fontWeight: 'bold',
        backgroundColor: style.bg,
        color: style.color
      }}>
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const handleReopen = async () => {
    if (!window.confirm('Are you sure you want to reopen this assessment? This will change the status to "reopened" and allow modifications.')) {
      return;
    }
    
    try {
      setActionLoading(true);
      await assessmentsApi.reopen(assessmentId, { reason: 'Reopened for modifications' });
      alert('Assessment reopened successfully!');
      loadAssessment(); // Reload to update status
    } catch (err) {
      alert('Failed to reopen assessment: ' + (err.response?.data?.error || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleResubmit = async () => {
    if (!window.confirm('Are you sure you want to resubmit this assessment? This will recalculate all scores.')) {
      return;
    }
    
    try {
      setActionLoading(true);
      await assessmentsApi.submit(assessmentId);
      alert('Assessment resubmitted successfully!');
      loadAssessment(); // Reload to update status and scores
    } catch (err) {
      alert('Failed to resubmit assessment: ' + (err.response?.data?.message || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleGeneratePdf = async (options) => {
    try {
      setActionLoading(true);
      
      // Build query parameters from options
      const params = new URLSearchParams({
        include_met: options.include_met.toString(),
        include_not_met: options.include_not_met.toString(),
        include_partially_met: options.include_partially_met.toString(),
        include_na: options.include_na.toString(),
        include_private_notes: options.include_private_notes.toString(),
        sort_by: options.sort_by
      });

      // Call API to generate PDF
      const response = await assessmentsApi.generatePdf(assessmentId, params.toString());
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `assessment_report_${assessment.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      alert('PDF report generated successfully!');
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF report: ' + (error.response?.data?.error || error.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDuplicate = async (options) => {
    try {
      setActionLoading(true);
      
      const response = await assessmentsApi.duplicate(assessmentId, options);
      
      alert(`Assessment duplicated successfully! New assessment: "${response.data.assessment.name}"`);
      
      // Navigate to the new assessment
      navigate(`/assessments/${response.data.assessment.id}`);
      
    } catch (error) {
      console.error('Error duplicating assessment:', error);
      alert('Failed to duplicate assessment: ' + (error.response?.data?.error || error.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`⚠️ WARNING: This will permanently delete "${assessment.name}" and ALL related data (responses, scores, history). This action CANNOT be undone!\n\nAre you absolutely sure?`)) {
      return;
    }
    
    try {
      setActionLoading(true);
      
      await assessmentsApi.delete(assessmentId);
      
      alert(`Assessment "${assessment.name}" has been permanently deleted.`);
      
      // Navigate back to assessments list
      navigate('/assessments');
      
    } catch (error) {
      console.error('Error deleting assessment:', error);
      if (error.response?.status === 403) {
        alert('Access denied: Only administrators can delete assessments.');
      } else {
        alert('Failed to delete assessment: ' + (error.response?.data?.error || error.message));
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateMetadata = async (data) => {
    try {
      await assessmentsApi.updateMetadata(assessmentId, data);
      
      // Show success message
      alert('Assessment details updated successfully');
      
      // Reload assessment data
      loadAssessment();
      
    } catch (error) {
      console.error('Error updating metadata:', error);
      throw error; // Re-throw so dialog can handle it
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error || !assessment) {
    return (
      <div>
        <h1>Error</h1>
        <p>{error || 'Assessment not found'}</p>
        <button onClick={() => navigate('/assessments')}>Back to Assessments</button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <h1 style={{ margin: 0 }}>{assessment.name}</h1>
          {getStatusBadge(assessment.status)}
        </div>
        <Link to="/assessments" style={{ color: '#3498db', textDecoration: 'none' }}>
          ← Back to Assessments
        </Link>
      </div>

      {/* Details Card */}
      <div style={{
        backgroundColor: 'white',
        padding: '30px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '20px'
      }}>
        <h2 style={{ marginTop: 0, marginBottom: '20px' }}>Assessment Details</h2>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div>
            <label style={labelStyle}>Client</label>
            <Link to={`/clients/${assessment.client_id}`} style={{ color: '#3498db', textDecoration: 'none', fontWeight: 'bold' }}>
              {assessment.client_name}
            </Link>
          </div>

          <div>
            <label style={labelStyle}>Assessment Type</label>
            <p style={valueStyle}>{assessment.assessment_type_name}</p>
          </div>

          <div>
            <label style={labelStyle}>Created</label>
            <p style={valueStyle}>{formatDate(assessment.created_at)}</p>
          </div>

          <div>
            <label style={labelStyle}>Last Updated</label>
            <p style={valueStyle}>{formatDate(assessment.updated_at)}</p>
          </div>

          {assessment.submitted_at && (
            <div>
              <label style={labelStyle}>Submitted</label>
              <p style={valueStyle}>{formatDate(assessment.submitted_at)}</p>
            </div>
          )}

          {assessment.description && (
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Description</label>
              <p style={valueStyle}>{assessment.description}</p>
            </div>
          )}
        </div>
      </div>

      {/* Progress Card */}
      <div style={{
        backgroundColor: 'white',
        padding: '30px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '20px'
      }}>
        <h2 style={{ marginTop: 0, marginBottom: '20px' }}>Progress</h2>

        <div style={{ marginBottom: '15px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
            <span style={{ fontWeight: 'bold' }}>Completion</span>
            <span style={{ fontWeight: 'bold', fontSize: '18px', color: '#27ae60' }}>
              {assessment.progress_percentage || 0}%
            </span>
          </div>
          <div style={{
            height: '20px',
            backgroundColor: '#e9ecef',
            borderRadius: '10px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${assessment.progress_percentage || 0}%`,
              height: '100%',
              backgroundColor: '#27ae60',
              transition: 'width 0.3s'
            }} />
          </div>
        </div>

        {assessment.total_questions > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginTop: '20px' }}>
            <div>
              <label style={labelStyle}>Total Questions</label>
              <p style={{ ...valueStyle, fontSize: '24px', fontWeight: 'bold', color: '#3498db' }}>
                {assessment.total_questions}
              </p>
            </div>
            <div>
              <label style={labelStyle}>Answered</label>
              <p style={{ ...valueStyle, fontSize: '24px', fontWeight: 'bold', color: '#27ae60' }}>
                {assessment.answered_questions || 0}
              </p>
            </div>
            <div>
              <label style={labelStyle}>Remaining</label>
              <p style={{ ...valueStyle, fontSize: '24px', fontWeight: 'bold', color: '#e67e22' }}>
                {assessment.total_questions - (assessment.answered_questions || 0)}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <button
          onClick={() => setEditMetadataDialogOpen(true)}
          disabled={actionLoading}
          style={{
            padding: '12px 24px',
            backgroundColor: '#95a5a6',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: actionLoading ? 'not-allowed' : 'pointer',
            fontSize: '16px',
            fontWeight: 'bold',
            opacity: actionLoading ? 0.6 : 1
          }}
        >
          ✏️ Edit Details
        </button>
        
        {assessment.status === 'draft' || assessment.status === 'in_progress' || assessment.status === 'reopened' ? (
          <>
            <button
              onClick={() => navigate(`/assessments/${assessmentId}/take`)}
              disabled={actionLoading}
              style={{
                padding: '12px 24px',
                backgroundColor: '#3498db',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: actionLoading ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                fontWeight: 'bold',
                opacity: actionLoading ? 0.6 : 1
              }}
            >
              {assessment.status === 'draft' ? 'Start Assessment' : 'Continue Assessment'}
            </button>
            {assessment.status === 'reopened' && (
              <button
                onClick={handleResubmit}
                disabled={actionLoading}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#27ae60',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: actionLoading ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  opacity: actionLoading ? 0.6 : 1
                }}
              >
                {actionLoading ? 'Submitting...' : '✓ Resubmit Assessment'}
              </button>
            )}
          </>
        ) : (
          <>
            <button
              onClick={() => navigate(`/assessments/${assessmentId}/results`)}
              style={{
                padding: '12px 24px',
                backgroundColor: '#27ae60',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 'bold'
              }}
            >
              View Results
            </button>
            <button
              onClick={() => setPdfDialogOpen(true)}
              disabled={actionLoading}
              style={{
                padding: '12px 24px',
                backgroundColor: '#e74c3c',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: actionLoading ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                fontWeight: 'bold',
                opacity: actionLoading ? 0.6 : 1
              }}
            >
              📄 Generate PDF Report
            </button>
            <button
              onClick={handleReopen}
              disabled={actionLoading}
              style={{
                padding: '12px 24px',
                backgroundColor: '#f39c12',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: actionLoading ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                fontWeight: 'bold',
                opacity: actionLoading ? 0.6 : 1
              }}
            >
              {actionLoading ? 'Reopening...' : '🔓 Reopen for Editing'}
            </button>
          </>
        )}

        <button
          onClick={() => setDuplicateDialogOpen(true)}
          disabled={actionLoading}
          style={{
            padding: '12px 24px',
            backgroundColor: '#9b59b6',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: actionLoading ? 'not-allowed' : 'pointer',
            fontSize: '16px',
            fontWeight: 'bold',
            opacity: actionLoading ? 0.6 : 1
          }}
        >
          📋 Duplicate Assessment
        </button>

        <button
          onClick={handleDelete}
          disabled={actionLoading}
          style={{
            padding: '12px 24px',
            backgroundColor: '#e74c3c',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: actionLoading ? 'not-allowed' : 'pointer',
            fontSize: '16px',
            fontWeight: 'bold',
            opacity: actionLoading ? 0.6 : 1
          }}
        >
          🗑️ Delete Assessment
        </button>

        <button
          onClick={() => navigate('/assessments')}
          style={{
            padding: '12px 24px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >
          Back to List
        </button>
      </div>

      {/* PDF Options Dialog */}
      <PdfOptionsDialog
        open={pdfDialogOpen}
        onClose={() => setPdfDialogOpen(false)}
        assessmentId={assessmentId}
        assessmentName={assessment.name}
        onGenerate={handleGeneratePdf}
      />

      {/* Duplicate Assessment Dialog */}
      <DuplicateAssessmentDialog
        open={duplicateDialogOpen}
        onClose={() => setDuplicateDialogOpen(false)}
        assessmentId={assessmentId}
        assessmentName={assessment.name}
        onDuplicate={handleDuplicate}
      />

      {/* Edit Assessment Metadata Dialog */}
      <EditAssessmentMetadataDialog
        open={editMetadataDialogOpen}
        onClose={() => setEditMetadataDialogOpen(false)}
        assessment={assessment}
        onSave={handleUpdateMetadata}
      />
    </div>
  );
};

const labelStyle = {
  display: 'block',
  fontSize: '12px',
  fontWeight: 'bold',
  color: '#666',
  textTransform: 'uppercase',
  marginBottom: '5px'
};

const valueStyle = {
  margin: 0,
  fontSize: '16px',
  color: '#333'
};

export default AssessmentDetails;
